import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, ShoppingCart, TrendingDown, Clock, Milk, ArrowRight, Sparkles, MapPin, LocateFixed, RefreshCw, Scale, Navigation, ScanLine } from "lucide-react";
import client, { money, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import ForYou from "@/components/ForYou";

const Card = ({ className = "", children, testId }) => (
  <div data-testid={testId} className={`${className.includes("bg-") ? "" : "bg-white"} rounded-3xl p-5 pp-shadow transition-transform duration-200 hover:-translate-y-0.5 ${className}`}>{children}</div>
);

const HOW = [
  ["/img/produce.jpg", ShoppingCart, "Build your list", "Type it the way you'd say it."],
  ["/img/market.jpg", Scale, "Compare every store", "Prices, sales, fuel and time."],
  ["/img/cart.jpg", Navigation, "Shop one smart route", "Straight into Google or Apple Maps."],
  ["/img/bakery.jpg", ScanLine, "Snap the receipt", "See exactly what you saved."],
];

export const DealCard = ({ d, testId, onAdd }) => (
  <div data-testid={testId} className="shrink-0 w-44 sm:w-48 snap-start bg-white rounded-2xl p-2.5 pp-shadow transition-transform duration-200 hover:-translate-y-1">
    <div className="h-32 rounded-xl bg-white grid place-items-center overflow-hidden border border-pp-border">
      <img src={d.image} alt={d.name} loading="lazy" className="max-h-full max-w-full object-contain" />
    </div>
    <p className="mt-2 text-sm font-bold text-pp-text line-clamp-2 min-h-[2.5rem]">{d.name}</p>
    <p className="text-xs text-pp-muted truncate">{d.merchant}</p>
    <div className="mt-1 flex items-baseline gap-1.5">
      <span className="text-lg font-extrabold text-pp-forest">{money(d.price)}</span>
      {d.unit_text && <span className="text-[10px] text-pp-muted truncate">{d.unit_text.slice(0, 18)}</span>}
    </div>
    {d.was_text && <p className="text-[11px] font-semibold text-pp-error truncate">{d.was_text}</p>}
    {onAdd && (
      <button type="button" onClick={() => onAdd(d)} data-testid={`${testId}-add`}
        className="mt-2 w-full rounded-full bg-pp-forest text-white text-xs font-bold py-1.5 transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]">
        + Add to my list
      </button>
    )}
  </div>
);

export async function addToList(d) {
  try {
    const { data: lists } = await client.get("/lists");
    const list = lists[0] || (await client.post("/lists", { name: "Weekly Groceries" })).data;
    await client.post(`/lists/${list.id}/items`, { raw: d.name });
    toast.success(`Added ${d.name} to ${list.name}`);
  } catch { toast.error("Couldn't add to your list"); }
}

function LocationPrompt({ prefs, onDone }) {
  const [busy, setBusy] = useState(false);
  const [place, setPlace] = useState("");
  if (prefs?.location_source === "device" || prefs?.location_source === "text") return null;
  const done = () => { toast.success("Got it — loading real stores and prices near you (about a minute)"); onDone(); };
  const byText = async (e) => {
    e.preventDefault();
    if (place.trim().length < 3) return;
    setBusy(true);
    try { await client.post("/live/locate-text", { query: place.trim() }); done(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const locate = () => {
    if (!navigator.geolocation) return toast.error("Location isn't available on this device");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try { await client.post("/live/locate", { lat: pos.coords.latitude, lng: pos.coords.longitude }); done(); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
    }, () => { setBusy(false); toast.error("Location permission was denied — enter your postal code instead"); });
  };
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 rounded-3xl bg-pp-gold/20 border border-pp-gold/40 p-4" data-testid="location-prompt">
      <span className="grid place-items-center h-10 w-10 rounded-2xl bg-pp-gold text-pp-text shrink-0"><LocateFixed className="h-5 w-5" /></span>
      <div className="flex-1"><p className="font-bold text-pp-text">Find stores and deals near you, anywhere in Canada</p>
        <p className="text-sm text-pp-muted">Share your location or enter your postal code so we compare the grocers around you.</p></div>
      <form onSubmit={byText} className="flex gap-2">
        <Input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Postal code or city" className="h-10 bg-white w-40" data-testid="location-text-input" />
        <Button type="submit" variant="outline" disabled={busy} className="rounded-full bg-white h-10" data-testid="location-text-submit">Set</Button>
      </form>
      <Button onClick={locate} disabled={busy} data-testid="use-location-button" className="rounded-full bg-pp-forest font-bold">{busy ? "Locating…" : "Use my location"}</Button>
    </div>
  );
}

export default function HomePage() {
  const { user, prefs, loadPrefs } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [deals, setDeals] = useState(null);
  const [q, setQ] = useState("");

  const loadDeals = useCallback(() => { setDeals(null); client.get("/live/deals").then((r) => setDeals(r.data)).catch(() => setDeals({ featured: [] })); }, []);
  useEffect(() => { client.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({})); loadDeals(); }, [loadDeals]);
  const addDeal = async (d) => { await addToList(d); client.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); };

  const buildList = async () => {
    if (data?.current_list) return navigate(`/list/${data.current_list.id}`);
    try { const { data: l } = await client.post("/lists", { name: "Weekly Groceries" }); navigate(`/list/${l.id}`); }
    catch { toast.error("Could not create list"); }
  };
  const search = (e) => { e.preventDefault(); if (q.trim()) navigate(`/discover?q=${encodeURIComponent(q)}`); };
  const first = (user?.name || "there").split(" ")[0];

  return (
    <div className="space-y-7">
      <div className="relative overflow-hidden rounded-3xl bg-pp-forest pp-shadow-lg pp-fade-up" data-testid="home-hero">
        <img src="/hero-groceries.jpg" alt="Fresh groceries" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#075B3A]/95 via-[#075B3A]/75 to-transparent" />
        <div className="relative p-6 sm:p-10 max-w-xl text-white">
          <p className="text-white/80 text-sm font-semibold flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {prefs?.city || "Set your location"}</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Hi {first}, what are we buying today?</h1>
          <p className="mt-3 text-white/85">We'll find where and when to buy everything for the best value.</p>
          <form onSubmit={search} className="relative mt-5">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-pp-muted" />
            <Input data-testid="home-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals, e.g. black diamond cheese"
              className="h-14 pl-12 rounded-2xl bg-white text-base text-pp-text border-0" />
          </form>
          <Button data-testid="build-list-button" onClick={buildList}
            className="mt-3 h-14 w-full sm:w-auto rounded-2xl bg-pp-gold text-pp-text hover:bg-pp-gold/90 px-6 text-base font-extrabold transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]">
            <ShoppingCart className="h-5 w-5 mr-2" /> {data?.current_list ? "Continue my grocery list" : "Build my grocery list"} <ArrowRight className="h-5 w-5 ml-1" />
          </Button>
        </div>
      </div>

      <LocationPrompt prefs={prefs} onDone={() => { loadPrefs(); loadDeals(); }} />

      <section data-testid="live-deals">
        <div className="flex items-end justify-between mb-3">
          <div><h2 className="text-xl font-extrabold text-pp-text">This week's flyer deals near you</h2>
            <p className="text-xs text-pp-muted">Real prices from current store flyers · a fresh mix every visit</p></div>
          <button onClick={loadDeals} data-testid="refresh-deals" className="flex items-center gap-1 text-sm font-semibold text-pp-forest hover:underline"><RefreshCw className="h-4 w-4" /> Shuffle</button>
        </div>
        <div className="flex gap-3 overflow-x-auto snap-x py-1 pb-3 scrollbar-none">
          {!deals ? [...Array(6)].map((_, i) => <Skeleton key={i} className="h-60 w-48 shrink-0 rounded-2xl" />)
            : deals.featured.length ? deals.featured.map((d, i) => <DealCard key={d.id} d={d} testId={`home-deal-${i}`} onAdd={addDeal} />)
            : <p className="text-sm text-pp-muted">No flyer deals found near you right now.</p>}
        </div>
      </section>

      <ForYou onAdded={() => client.get("/dashboard").then((r) => setData(r.data)).catch(() => {})} />

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="how-it-works">
        {HOW.map(([img, Icon, t, s], i) => (
          <div key={t} className="relative overflow-hidden rounded-3xl h-40 pp-shadow">
            <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#17312A]/90 via-[#17312A]/40 to-transparent" />
            <div className="relative h-full p-4 flex flex-col justify-end text-white">
              <span className="text-[11px] font-bold text-pp-gold">STEP {i + 1}</span>
              <p className="font-extrabold flex items-center gap-1.5"><Icon className="h-4 w-4" /> {t}</p>
              <p className="text-xs text-white/80">{s}</p>
            </div>
          </div>
        ))}
      </section>

      {!data ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-3xl" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Card testId="card-weekly-savings" className="bg-pp-forest text-white">
            <div className="flex items-center gap-2 text-white/80 text-sm font-semibold"><TrendingDown className="h-4 w-4" /> Your savings</div>
            {data.week_saved > 0 ? (
              <><p className="text-4xl font-extrabold mt-3 text-pp-gold">{money(data.week_saved)}</p>
                <p className="text-white/70 text-sm">saved this week · {money(data.month_saved)} this month</p></>
            ) : data.current_list?.potential_saving > 0 ? (
              <><p className="text-4xl font-extrabold mt-3 text-pp-gold">{money(data.current_list.potential_saving)}</p>
                <p className="text-white/70 text-sm">you could save on your current list with the Best Overall plan</p></>
            ) : data.lifetime_saved > 0 ? (
              <><p className="text-4xl font-extrabold mt-3 text-pp-gold">{money(data.lifetime_saved)}</p>
                <p className="text-white/70 text-sm">saved so far across {data.trips_count} trips</p></>
            ) : (
              <><p className="text-2xl font-extrabold mt-3 text-pp-gold">Start saving today</p>
                <p className="text-white/70 text-sm">Build a list and we'll show how much you can save at stores near you.</p></>
            )}
            <button onClick={() => navigate("/savings")} className="mt-3 text-sm font-bold text-white hover:underline" data-testid="home-view-savings">See budget & insights →</button>
          </Card>
          <Card testId="card-current-list">
            <div className="flex items-center gap-2 text-pp-muted text-sm font-semibold"><ShoppingCart className="h-4 w-4 text-pp-forest" /> Current List</div>
            {data.current_list ? (
              <>
                <p className="font-bold text-pp-text mt-3 text-lg">{data.current_list.name}</p>
                <p className="text-sm text-pp-muted">{data.current_list.item_count} items · est. {data.current_list.est_total ? money(data.current_list.est_total) : "—"}</p>
                <Button data-testid="continue-planning" onClick={() => navigate(`/list/${data.current_list.id}`)}
                  className="mt-3 rounded-full bg-pp-fresh text-pp-text hover:bg-pp-fresh/90 font-bold">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
              </>
            ) : <p className="mt-3 text-pp-muted text-sm">No active list yet — tap Build my grocery list.</p>}
          </Card>
          <Card testId="card-pantry-reminders">
            <div className="flex items-center gap-2 text-pp-muted text-sm font-semibold"><Milk className="h-4 w-4 text-pp-gold" /> Pantry Reminders</div>
            {data.pantry_reminders?.length ? (
              <ul className="mt-3 space-y-2">
                {data.pantry_reminders.slice(0, 3).map((p) => (
                  <li key={p.id} className="flex items-center gap-2 text-sm text-pp-text"><span className="h-2 w-2 rounded-full bg-pp-gold" /> Running low on <b>{p.name}</b></li>
                ))}
              </ul>
            ) : <p className="mt-3 text-pp-muted text-sm">Your pantry looks well stocked.</p>}
            <button onClick={() => navigate("/pantry")} className="mt-3 text-sm font-semibold text-pp-forest hover:underline">Open pantry →</button>
          </Card>
          <Card testId="card-shopping-window">
            <div className="flex items-center gap-2 text-pp-muted text-sm font-semibold"><Clock className="h-4 w-4 text-pp-info" /> Best time to shop</div>
            <p className="mt-3 font-bold text-pp-text">{data.shopping_window}</p>
          </Card>
          <Card testId="card-recent-trips" className="md:col-span-2">
            <div className="flex items-center gap-2 text-pp-muted text-sm font-semibold"><Sparkles className="h-4 w-4 text-pp-fresh" /> Recent Trips</div>
            {data.recent_trips?.length ? (
              <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
                {data.recent_trips.slice(0, 4).map((t) => (
                  <li key={t.id} className="flex items-center justify-between text-sm rounded-2xl bg-pp-cream p-3">
                    <div><p className="font-semibold text-pp-text">{t.num_stores} store{t.num_stores > 1 ? "s" : ""}</p><p className="text-pp-muted text-xs">{new Date(t.date).toLocaleDateString()}</p></div>
                    <div className="text-right"><p className="font-bold text-pp-text">{money(t.actual_total)}</p><p className="text-pp-forest text-xs font-semibold">saved {money(t.saved)}</p></div>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-3 text-pp-muted text-sm">No trips yet.</p>}
          </Card>
        </div>
      )}
    </div>
  );
}
